import { DOCUMENT, Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'em-theme';

/**
 * Applies the EMSYS design-system theme: light is the default (:root tokens),
 * dark ("Control Room") is activated by setting data-theme="dark" on <body>,
 * which flips every theme-aware --em-* token.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  readonly theme$ = new BehaviorSubject<Theme>(this.storedTheme());

  constructor() {
    this.applyTheme(this.theme$.value);
  }

  setTheme(theme: Theme): void {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Persistence is best-effort; the theme still applies for this session.
    }

    this.applyTheme(theme);

    if (this.theme$.value !== theme) {
      this.theme$.next(theme);
    }
  }

  private applyTheme(theme: Theme): void {
    if (theme === 'dark') {
      this.document.body.setAttribute('data-theme', 'dark');
    } else {
      this.document.body.removeAttribute('data-theme');
    }
  }

  private storedTheme(): Theme {
    try {
      return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  }
}
